import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Footer from "../../Components/Footer/Footer";
import siteConfig from "../../config/site";

const API_URL = siteConfig.apiUrl;

const Join = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");

  const [status, setStatus] = useState("loading"); // loading | valid | invalid | expired | used | no-token
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!inviteToken) {
      setStatus("no-token");
      return;
    }

    // Validate the invite
    fetch(`${API_URL}/invites/validate/${inviteToken}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) {
          setStatus("valid");
          setInviteData(data);
        } else if (data.error === "Invite already used") {
          setStatus("used");
        } else if (data.error === "Invite expired") {
          setStatus("expired");
        } else {
          setStatus("invalid");
        }
      })
      .catch(() => {
        setStatus("invalid");
        setError("Unable to verify invite. Please try again.");
      });
  }, [inviteToken]);

  const handleGoogleJoin = () => {
    // Pass invite token to OAuth flow
    window.location.href = `${API_URL}/auth/google?invite=${inviteToken}`;
  };

  // Loading
  if (status === "loading") {
    return (
      <div className="bg-[#0a0a0f] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400 font-body text-sm">Verifying your invite...</p>
        </div>
      </div>
    );
  }

  // No token
  if (status === "no-token") {
    return (
      <div className="bg-[#0a0a0f] min-h-screen">
        <div className="h-20" />
        <section className="py-20 px-6">
          <div className="max-w-md mx-auto text-center">
            <h1 className="font-heading text-3xl font-bold text-white mb-4">No Invite Found</h1>
            <p className="text-gray-400 font-body mb-8">
              You need an invite link to join Exo Entertainment as a performer.
            </p>
            <Link to="/hiring" className="text-purple-400 hover:text-purple-300 transition-colors font-body">
              Interested in joining? Apply here →
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  // Invalid / Expired / Used
  if (status === "invalid" || status === "expired" || status === "used") {
    const messages = {
      invalid: { title: "Invalid Invite", desc: "This invite link is not valid. Please contact the admin for a new one." },
      expired: { title: "Invite Expired", desc: "This invite has expired. Please ask the admin to send a new one." },
      used: { title: "Already Used", desc: "This invite has already been used. If you've already joined, sign in instead." },
    };
    const msg = messages[status];

    return (
      <div className="bg-[#0a0a0f] min-h-screen">
        <div className="h-20" />
        <section className="py-20 px-6">
          <div className="max-w-md mx-auto text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h1 className="font-heading text-3xl font-bold text-white mb-4">{msg.title}</h1>
            <p className="text-gray-400 font-body mb-8">{msg.desc}</p>
            <div className="space-y-3">
              <Link to="/login" className="block text-purple-400 hover:text-purple-300 transition-colors font-body">
                Sign in →
              </Link>
              <Link to="/" className="block text-gray-600 hover:text-gray-400 transition-colors font-body text-sm">
                ← Back to site
              </Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  // Valid invite — show join page
  return (
    <div className="bg-[#0a0a0f] min-h-screen">
      <div className="h-20" />

      <section className="py-20 px-6">
        <div className="max-w-md mx-auto">
          {/* Brand */}
          <div className="text-center mb-10">
            <div className="text-5xl mb-4">✨</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-2">
              You're Invited
            </h1>
            <div className="w-16 h-0.5 bg-gradient-to-r from-purple-700 to-gold mx-auto mb-4" />
            <p className="text-gray-400 font-body">
              Join Exo Entertainment as a performer.
            </p>
          </div>

          {/* Invite details */}
          <div className="bg-gray-900/40 border border-gray-800/50 rounded-xl p-6 mb-8">
            {inviteData.performerName && (
              <div className="mb-3">
                <span className="text-gray-500 text-xs uppercase tracking-wider font-body">Welcome,</span>
                <p className="text-white font-heading text-xl">{inviteData.performerName}</p>
              </div>
            )}
            {inviteData.personaNames?.length > 0 && (
              <div className="mb-3">
                <span className="text-gray-500 text-xs uppercase tracking-wider font-body">Your Persona{inviteData.personaNames.length > 1 ? "s" : ""}</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {inviteData.personaNames.map(name => (
                    <span key={name} className="px-3 py-1 bg-purple-900/40 border border-purple-700/30 rounded-full text-purple-300 text-sm font-body">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <span className="text-gray-500 text-xs uppercase tracking-wider font-body">Sign in with</span>
              <p className="text-gray-300 font-body text-sm mt-1">{inviteData.email}</p>
            </div>
          </div>

          {/* Google Sign In */}
          <button
            onClick={handleGoogleJoin}
            className="w-full flex items-center justify-center gap-3 py-3 px-6 bg-white text-gray-800 font-semibold text-sm rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google to Join
          </button>

          <p className="text-center text-gray-600 text-xs mt-4 font-body">
            Please use <span className="text-gray-400">{inviteData.email}</span> to sign in.
          </p>

          {/* Footer links */}
          <div className="text-center mt-10">
            <p className="text-gray-700 text-xs font-body">
              <Link to="/" className="hover:text-gray-500 transition-colors">
                ← Back to site
              </Link>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Join;
